import type {
  CalendarEventPublic,
  GraphRelationshipReminder,
  SaleskoScheduleResponse,
  ScheduleOpportunityFollowUpItem
} from "@salesko/contracts";
import { BellPlus, CalendarDays, CalendarRange, Loader2, RefreshCw } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useI18n } from "../../i18n";
import { compactDateTime } from "../lib/format";

type SchedulePanelProps = {
  data: SaleskoScheduleResponse | undefined;
  enabled: boolean;
  error: string | undefined;
  pending: boolean;
};

const groupStyle: CSSProperties = {
  display: "grid",
  gap: "9px",
  borderTop: "1px solid var(--border-subtle)",
  paddingTop: "12px"
};

const groupHeaderStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px"
};

const groupTitleStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  margin: 0,
  color: "var(--text-strong)",
  fontFamily: "var(--font-display)",
  fontSize: "var(--text-body-md)"
};

const countStyle: CSSProperties = {
  color: "var(--text-muted)",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--text-micro)"
};

const metaStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "6px 10px",
  margin: 0,
  color: "var(--text-muted)",
  fontSize: "var(--text-caption)"
};

const truncationStyle: CSSProperties = {
  margin: 0,
  color: "var(--warning-text, var(--text-secondary))",
  fontSize: "var(--text-caption)"
};

export function scheduleRangeForLocalDays(now = new Date(), dayCount = 14) {
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + dayCount);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function scheduleQueryKey(tenantId: string | null, range: { from: string; to: string }) {
  return ["schedule", tenantId, range.from, range.to] as const;
}

export function workspaceViewAfterFrameUpdate(currentView: string, frameView: string) {
  return currentView === "overview" || currentView === "help" || currentView === "current" || currentView === "schedule" || currentView === "privateAgent"
    ? currentView
    : frameView;
}

export function SchedulePanel({ data, enabled, error, pending }: SchedulePanelProps) {
  const { t } = useI18n();
  const totalCount = data
    ? data.calendarEvents.count + data.opportunityFollowUps.count + data.relationshipReminders.count
    : 0;

  return (
    <div className="signals-panel schedule-panel">
      <header className="signals-header">
        <span className="signals-mark" aria-hidden="true">
          <CalendarRange size={20} />
        </span>
        <div>
          <p className="eyebrow">{t.schedule.eyebrow}</p>
          <h2>{t.schedule.title}</h2>
          <span>
            {data
              ? t.schedule.range(compactDateTime(data.range.from), compactDateTime(data.range.to))
              : t.schedule.items(totalCount)}
          </span>
        </div>
      </header>

      {!enabled ? (
        <p className="signals-empty">{t.schedule.signInToLoad}</p>
      ) : pending ? (
        <div className="signals-loading">
          <Loader2 className="spin" size={18} />
          <span>{t.schedule.loading}</span>
        </div>
      ) : error ? (
        <p className="signals-error" role="alert">{t.schedule.unavailable}</p>
      ) : !data ? (
        <p className="signals-error" role="alert">{t.schedule.unavailable}</p>
      ) : (
        <div className="signals-list">
          <ScheduleGroup
            count={data.calendarEvents.count}
            empty={t.schedule.calendarEmpty}
            icon={<CalendarDays size={16} />}
            title={t.schedule.calendarTitle}
            truncated={data.calendarEvents.truncated}
          >
            {data.calendarEvents.items.map((event) => (
              <CalendarEventCard event={event} key={event.id} />
            ))}
          </ScheduleGroup>

          <ScheduleGroup
            count={data.opportunityFollowUps.count}
            empty={t.schedule.followUpsEmpty}
            icon={<BellPlus size={16} />}
            title={t.schedule.followUpsTitle}
            truncated={data.opportunityFollowUps.truncated}
          >
            {data.opportunityFollowUps.items.map((item) => (
              <FollowUpCard item={item} key={item.opportunity.id} />
            ))}
          </ScheduleGroup>

          <ScheduleGroup
            count={data.relationshipReminders.count}
            empty={t.schedule.remindersEmpty}
            icon={<RefreshCw size={16} />}
            title={t.schedule.remindersTitle}
            truncated={data.relationshipReminders.truncated}
          >
            {data.relationshipReminders.items.map((item) => (
              <ReminderCard item={item} key={item.edge.id} />
            ))}
          </ScheduleGroup>
        </div>
      )}
    </div>
  );
}

function ScheduleGroup({
  children,
  count,
  empty,
  icon,
  title,
  truncated
}: {
  children: ReactNode;
  count: number;
  empty: string;
  icon: ReactNode;
  title: string;
  truncated: boolean;
}) {
  const { t } = useI18n();
  return (
    <section style={groupStyle}>
      <header style={groupHeaderStyle}>
        <h3 style={groupTitleStyle}>{icon}{title}</h3>
        <span style={countStyle}>{t.schedule.items(count)}</span>
      </header>
      {count === 0 ? <p className="signals-empty">{empty}</p> : children}
      {truncated ? <p style={truncationStyle}>{t.schedule.truncated}</p> : null}
    </section>
  );
}

function CalendarEventCard({ event }: { event: CalendarEventPublic }) {
  const { t } = useI18n();
  return (
    <article className="signal-card">
      <div className="signal-card-top">
        <span className={`signal-status signal-status-${event.status}`}>{event.status}</span>
        <span className="signal-score">{event.provider}</span>
      </div>
      <h3>{event.summary ?? t.schedule.untitledEvent}</h3>
      <p style={metaStyle}>{calendarEventTime(event, t.schedule.timeUnavailable)}</p>
      {event.location ? <p style={metaStyle}>{event.location}</p> : null}
    </article>
  );
}

function FollowUpCard({ item }: { item: ScheduleOpportunityFollowUpItem }) {
  const { t } = useI18n();
  return (
    <article className="signal-card">
      <div className="signal-card-top">
        <span className="signal-status signal-status-active">{item.opportunity.status}</span>
        <span className="signal-score">{Math.round(item.opportunity.score * 100)}</span>
      </div>
      <h3>{item.opportunity.title}</h3>
      <p>{item.opportunity.summary}</p>
      <p style={metaStyle}>{t.schedule.followUpRequested(compactDateTime(item.followUpRequestedAt))}</p>
    </article>
  );
}

function ReminderCard({ item }: { item: GraphRelationshipReminder }) {
  const { t } = useI18n();
  const source = item.sourceNode?.label ?? item.edge.source;
  const target = item.targetNode?.label ?? item.edge.target;
  return (
    <article className="signal-card">
      <div className="signal-card-top">
        <span className="signal-status signal-status-active">{item.edge.relationType}</span>
        <span className="signal-score">{Math.round(item.score * 100)}</span>
      </div>
      <h3>{item.edge.label}</h3>
      <p>{source} → {target}</p>
      <p style={metaStyle}>
        <span>{t.schedule.lastSeen(compactDateTime(item.lastSeenAt))}</span>
        <span>{t.schedule.staleByDays(item.staleByDays)}</span>
      </p>
    </article>
  );
}

function calendarEventTime(event: CalendarEventPublic, unavailable: string) {
  if (!event.startAt) {
    return unavailable;
  }
  const start = compactDateTime(event.startAt);
  return event.endAt ? `${start} – ${compactDateTime(event.endAt)}` : start;
}
